import {
  ColumnLayout,
  ExpandableSection,
  Box,
  KeyValuePairsProps,
  StatusIndicator,
  KeyValuePairs,
} from "@amzn/awsui-components-console";
import React, { useLayoutEffect } from "react";
import {
  FormFieldConfigType,
  FormStateType,
  INPUT_TYPES,
} from "src/forms/types";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

import { RootState } from "src/redux/store";
import { FILE_ATTACHMENTS_CONSTANTS } from "src/forms/constants";
import { cloneDeep, isEqual } from "lodash";
import { setFormsStateFirstLoad } from "./reviewPageSlice";
import { convertBytesTo } from "src/forms/FileUpload/FileUploadUtilities";
import { FileSize } from "src/forms/FileUpload/types";
import WcReviewPageContents from "../WorkersCompensation/WcReviewPageContents";
import { LineOfBusiness } from "../../app/types";
import CargoReviewPageContents from "../Cargo/CargoReviewPageContents";
import IPLReviewPageContents from "../IPL/IPLReviewPageContents";
import { checkIfAncestorsAreHidden } from "../tools";
import { EMPTY_STRING_INDICATOR } from "../../app/constants";

interface ReviewPageProps {
  allFormConfigs: { [key: string]: FormFieldConfigType[] };
}
export interface ReviewPageContentsProps {
  allFormConfigs: { [key: string]: FormFieldConfigType[] };
  renderFieldGroup: (
    label: string,
    fieldsToRender: FormFieldConfigType[],
    formState: FormStateType,
    formName: string,
    options?: {
      expandable?: boolean;
      hidden?: boolean;
      startAtField?: string;
      stopBeforeField?: string;
      columns?: number;
    }
  ) => React.ReactElement | null;
  renderFileList: (
    formState: FormStateType,
    fieldConfigs?: FormFieldConfigType[]
  ) => React.ReactElement | null;
}
const ReviewPage = ({ allFormConfigs }: ReviewPageProps) => {
  const dispatch = useAppDispatch();
  //Redux Forms state
  const { allFormStates } = useAppSelector((state: RootState) => state.forms);
  const { formsStateFirstLoad, formsStateSaved } = useAppSelector(
    (state: RootState) => state.reviewPage
  );
  const { lineOfBusiness } = useAppSelector((state: RootState) => state.app);

  useLayoutEffect(() => {
    if (!formsStateSaved) {
      dispatch(setFormsStateFirstLoad({ value: cloneDeep(allFormStates) }));
    }
  }, []);

  const shouldSkipField = (
    fieldConfig: FormFieldConfigType,
    formState: FormStateType,
    options?: { startAtField?: string; stopBeforeField?: string }
  ) => {
    const skipTypes = [INPUT_TYPES.BUTTON, INPUT_TYPES.FIELD_GROUP];
    const fieldState = formState[fieldConfig.uniqueName || fieldConfig.name];
    if (
      options?.startAtField &&
      !(
        fieldConfig.uniqueName === options?.startAtField ||
        fieldConfig.name == options?.startAtField
      )
    ) {
      return true;
    }
    delete options?.startAtField;
    if (
      fieldState?.hidden ||
      checkIfAncestorsAreHidden(formState, fieldConfig) ||
      skipTypes.includes(fieldConfig.type)
    ) {
      return true;
    }
    return false;
  };

  const handleSpecialFieldTypes = (
    fieldConfig: FormFieldConfigType,
    value: any
  ) => {
    if (fieldConfig.type === INPUT_TYPES.SELECT) {
      value = value.label;
    }
    if (fieldConfig.type === INPUT_TYPES.MULTISELECT) {
      value = value.map((entry: { value: string; label: string }) => {
        return (
          <span key={entry.label}>
            {entry.label}
            <br></br>
          </span>
        );
      });
    }

    if (fieldConfig.type === INPUT_TYPES.UPLOAD) {
      value = value?.length ? (
        renderFiles(value)
      ) : (
        <Box fontSize="body-m" color="text-status-warning">
          No Files uploaded
        </Box>
      );
      return (
        <React.Fragment>
          {value}
          <br />
        </React.Fragment>
      );
    }
    if (["true", true].includes(value)) {
      value = "Yes";
    }
    if (["false", false].includes(value)) {
      value = "No";
    }

    return value;
  };

  const fieldsToKeyValuePairs = (
    fieldsToRender: FormFieldConfigType[],
    formState: FormStateType,
    formName: string,
    options?: { startAtField?: string; stopBeforeField?: string }
  ) => {
    const items: KeyValuePairsProps.Pair[] = [];
    let editedLabel: React.ReactElement | null = null;
    const initialFormState = formsStateFirstLoad?.[formName];
    for (const fieldConfig of fieldsToRender) {
      if (
        options?.stopBeforeField &&
        (fieldConfig.uniqueName === options?.stopBeforeField ||
          fieldConfig.name === options?.stopBeforeField)
      ) {
        return items;
      }
      const fieldState = formState[fieldConfig.uniqueName || fieldConfig.name];
      if (shouldSkipField(fieldConfig, formState, options)) {
        continue;
      }
      const initialFieldState =
        initialFormState?.[fieldConfig.uniqueName || fieldConfig.name];
      let { value } = fieldState;

      value = handleSpecialFieldTypes(fieldConfig, value);

      if (
        initialFieldState &&
        !isEqual(initialFieldState.value, fieldState.value)
      ) {
        editedLabel = <StatusIndicator type="info">Edited</StatusIndicator>;
      }

      items.push({
        label: fieldConfig.label,
        value: value ?? EMPTY_STRING_INDICATOR,
        info: editedLabel,
      });
      editedLabel = null;
    }
    return items;
  };
  const renderFieldGroup = (
    label: string,
    fieldsToRender: FormFieldConfigType[],
    formState: FormStateType,
    formName: string,
    options?: {
      expandable?: boolean;
      hidden?: boolean;
      startAtField?: string;
      stopBeforeField?: string;
      columns?: number;
    }
  ) => {
    if (options?.hidden) {
      return null;
    }
    const items: KeyValuePairsProps.Pair[] = fieldsToKeyValuePairs(
      fieldsToRender,
      formState,
      formName,
      {
        startAtField: options?.startAtField,
        stopBeforeField: options?.stopBeforeField,
      }
    );

    const keyValuePairs = (
      <KeyValuePairs
        items={items}
        columns={options?.columns ?? 2}
      ></KeyValuePairs>
    );
    if (options?.expandable) {
      return (
        <ExpandableSection
          headerText={label}
          variant="default"
          defaultExpanded={true}
        >
          {keyValuePairs}
        </ExpandableSection>
      );
    } else {
      return keyValuePairs;
    }
  };
  const renderFiles = (files: any[]) => {
    const result = [];
    for (const file of files) {
      result.push(
        <ColumnLayout columns={2} variant="text-grid" key={file.name}>
          <div>
            <Box variant="awsui-key-label">File Name</Box>
            <div>{file.name}</div>
          </div>

          <div>
            <Box variant="awsui-key-label">Size</Box>
            <div>{`${convertBytesTo(file.size, FileSize.MB).toFixed(2)} ${
              FileSize.MB
            }`}</div>
          </div>
        </ColumnLayout>
      );
    }
    return result;
  };
  const renderFileList = (
    formState: FormStateType,
    fieldConfigs?: FormFieldConfigType[]
  ) => {
    if (!fieldConfigs?.length) {
      return null;
    }
    const fileUploadFieldConfigs = fieldConfigs.filter((field) => {
      return field.type === INPUT_TYPES.UPLOAD;
    });
    return renderFieldGroup(
      "",
      fileUploadFieldConfigs,
      formState,
      FILE_ATTACHMENTS_CONSTANTS.FORM_NAME,
      { columns: 1 }
    );
  };
  if (lineOfBusiness === LineOfBusiness.WORKERS_COMPENSATION) {
    return (
      <WcReviewPageContents
        renderFieldGroup={renderFieldGroup}
        renderFileList={renderFileList}
        allFormConfigs={allFormConfigs}
      ></WcReviewPageContents>
    );
  } else if (lineOfBusiness === LineOfBusiness.IPL) {
    return (
      <IPLReviewPageContents
        renderFieldGroup={renderFieldGroup}
        renderFileList={renderFileList}
        allFormConfigs={allFormConfigs}
      ></IPLReviewPageContents>
    );
  } else {
    return (
      <CargoReviewPageContents
        renderFieldGroup={renderFieldGroup}
        renderFileList={renderFileList}
        allFormConfigs={allFormConfigs}
      ></CargoReviewPageContents>
    );
  }
};
export default ReviewPage;
